import { useId, useState } from "react";
import {
  ApiError,
  SYMPTOM_CHECKLIST,
  SYMPTOM_CHECKLIST_VERSION,
  SYMPTOM_FREE_TEXT_MAX,
  type FullCheckinRequest,
  type SymptomId,
} from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Button, Text, Textarea, ToggleChip } from "@ferrivia/ui";
import { useSymptomLabels } from "./useSymptomLabels";
import s from "./SymptomCheckin.module.css";

// No counter until the field is nearly full — a running count from the first
// keystroke reads as a constraint on a free-text box that is meant to feel
// open-ended.
const FREE_TEXT_COUNTER_THRESHOLD = 100;

export interface FullSymptomCheckProps {
  onSaved: () => void;
}

export function FullSymptomCheck({ onSaved }: FullSymptomCheckProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("client");
  const freeTextId = useId();
  // See QuickCheckin's note: generated once per mount so a retry reuses the
  // same idempotency key.
  const [clientCheckinId] = useState(() => crypto.randomUUID());
  const [selected, setSelected] = useState<Set<SymptomId>>(new Set());
  const [noneOfThese, setNoneOfThese] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSave = selected.size > 0 || noneOfThese;
  const remaining = SYMPTOM_FREE_TEXT_MAX - freeText.length;

  const symptomLabels = useSymptomLabels();

  function toggleSymptom(id: SymptomId) {
    setNoneOfThese(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleNoneOfThese() {
    setNoneOfThese((prev) => {
      const next = !prev;
      if (next) {
        setSelected(new Set());
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setFailed(false);
    tracker.track("symptoms.save_attempted", { kind: "full" });
    const request: FullCheckinRequest = {
      kind: "full",
      clientCheckinId,
      checklistVersion: SYMPTOM_CHECKLIST_VERSION,
      symptoms: Array.from(selected),
      noneOfThese,
      // Trimmed-empty is "no free text", not "an empty string of free text".
      freeText: freeText.trim().length > 0 ? freeText : null,
    };
    try {
      await api.symptoms.saveFull(request);
      onSaved();
    } catch (err) {
      tracker.track("symptoms.save_failed", { kind: "full", reason: err instanceof ApiError ? err.code : "network" });
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={s.body}>
      {/* Ids and the checklistVersion are the versioned contract that a saved
          answer is pinned to (see api-contract/symptoms.ts) — that's what
          keeps a stored row interpretable after the wording changes. The
          *display* text is still translated per-locale here, keyed by id,
          with the contract's Finnish string as the fallback/default. */}
      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          {t("symptomCheckin.question", "Oletko viimeisen viikon aikana kärsinyt seuraavista oireista?")}
        </legend>
        <div className={s.chipGrid}>
          {SYMPTOM_CHECKLIST.map((entry) => (
            <ToggleChip key={entry.id} className={s.chip} checked={selected.has(entry.id)} onChange={() => toggleSymptom(entry.id)}>
              {symptomLabels[entry.id]}
            </ToggleChip>
          ))}
        </div>
        <hr className={s.rule} />
        <div className={s.chipGrid}>
          <ToggleChip className={s.chip} checked={noneOfThese} onChange={toggleNoneOfThese}>
            {t("symptomCheckin.noneOfThese", "Ei mitään näistä")}
          </ToggleChip>
        </div>
      </fieldset>
      <label htmlFor={freeTextId} className={s.freeTextLabel}>
        {t("symptomCheckin.freeTextLabel", "Muuta, mitä haluat kertoa?")}
      </label>
      <Textarea
        id={freeTextId}
        value={freeText}
        onChange={(event) => setFreeText(event.target.value)}
        maxLength={SYMPTOM_FREE_TEXT_MAX}
        maxHeight={160}
      />
      {remaining <= FREE_TEXT_COUNTER_THRESHOLD && (
        <Text as="div" size="xs" className={s.counter}>
          {remaining}
        </Text>
      )}
      {failed && (
        <Text as="p" size="sm" className={s.error}>
          {t("symptomCheckin.saveFailed", "Tallennus epäonnistui. Yritä uudestaan.")}
        </Text>
      )}
      <div className={s.saveBar}>
        <Button onClick={handleSave} disabled={!canSave || saving}>
          {t("symptomCheckin.save", "Tallenna")}
        </Button>
      </div>
    </div>
  );
}
